import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import { Box, Checkbox, Chip, FormControl, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAtom, useAtomValue } from "jotai";
import {
  MaterialReactTable,
  MRT_RowVirtualizer,
  useMaterialReactTable,
  type MRT_ColumnDef
} from "material-react-table";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import moment from "moment";
import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  breakoutLinksColumns,
  renderFindingTypes,
  renderRatingColumn
} from ".";
import { aiIndicationIconsAvatar } from "../../../lib/teacup/lib/ai-indicator";
import LinkIcon from "../../../icons/LinkIcon";
import useDashJSPlayer from "../../hooks/useDashJSPlayer";
import { selectedInsightRowAtom } from "../../stores/jotai/insights/selectedInsightRow";
import { GetFindingTableResponse } from "../../tanstackQuery/queries/findings/type";
import { getDateFormat } from "../../util";
import {
  getQuote,
  handleExport,
  priorityBadge
} from "../../util/insights/handle-export";
import MaterialAvatar from "../general/MaterialAvatar";
import CrossIcon from "../../../icons/CrossIcon";
import PlayAudioIcon from "../../../icons/PlayAudioIcon";
import PauseAudioIcon from "../../../icons/PauseAudioIcon";
import { artifactSourceTypeMap } from "../../constants";

interface InsightAudioProps {
  finding: GetFindingTableResponse;
  isDetails?: boolean;
  selected?:boolean
}

interface InsightTableProps {
  tableData: GetFindingTableResponse[];
  rowVirtualizerInstanceRef: MutableRefObject<MRT_RowVirtualizer>;
  artifactDetails:Object;
  setMatchedInsightGroup?:Function;
}

type MultiSelectFilterProps = {
  column: any;
  placeholder?: string;
};

export const MultiSelectFilter = ({
  column,
  placeholder = "Select values",
}: MultiSelectFilterProps) => {
  const [hoveredAll, setHoveredAll] = useState(false);

  const filterValue = column.getFilterValue() ?? [];
  const setFilterValue = column.setFilterValue;

  const facetedValues = column.getFacetedUniqueValues?.() ?? new Map();
  const options = Array.from(facetedValues.keys())
    .filter((val) => val !== null && val !== undefined && val !== "")
    .sort();
    
  let chipWidthForColumn = "100px";
  if(column?.columnDef?.header==="Source"){
    chipWidthForColumn = "60px"
  }
  else if(column?.columnDef?.header==="Links"){
    chipWidthForColumn = "40px"
  }

  return (
    <FormControl
      sx={{
        minHeight: "38px",
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "4px",
          whiteSpace: "normal",
        },
        pt: 0.2,
      }}
      fullWidth
      variant="standard"
      size="small"
    >
      <Select
        multiple
        displayEmpty
        value={filterValue}
        onChange={(event) => {
          const value = event.target.value;
          setFilterValue(typeof value === "string" ? value.split(",") : value);
        }}
        renderValue={(selected) => {
          if (!selected?.length) {
            return (
              <Typography
                sx={{
                  font: "inherit",
                }}
                color="text.disabled"
              >
                {placeholder}
              </Typography>
            );
          }

          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => (
                <Tooltip title={value}>
                  <Chip
                  key={value}
                  label={value}
                  sx={{
                    maxWidth: `${chipWidthForColumn}`,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDelete={(e) => {
                    e.stopPropagation();
                    const newValue = filterValue.filter(
                      (item) => item !== value
                    );
                    setFilterValue(newValue.length ? newValue : undefined);
                  }}
                />
                </Tooltip>
              ))}
            </Box>
          );
        }}
        sx={{
          paddingBottom: "1px",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 250,
              maxWidth: 300,
            },
          },
        }}
        IconComponent={() => null}
        endAdornment={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pr: 1,
            }}
          >
            {filterValue.length > 1 && (
              <CrossIcon
                hovered={hoveredAll}
                onMouseEnter={() => setHoveredAll(true)}
                onMouseLeave={() => setHoveredAll(false)}
                sx={{
                  // margin: "0 4px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterValue(undefined);
                }}
              />
            )}
            {/* <ArrowDropDownIcon sx={{ color: "#888" }} /> */}
          </Box>
        }
      >
        { 
          options?.length ? (
            options.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={filterValue.includes(option)} />
                <ListItemText primary={option} />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <ListItemText primary="No data available" />
            </MenuItem>
          )
        }
      </Select>
    </FormControl>
  );
};

export function InsightAudio({
  finding,
  isDetails = false,
  selected
}: InsightAudioProps) {
  const [playedFindingId, setPlayedFindingId] = useState(null);
  const { dashJSPlayer } = useDashJSPlayer();
  const selectedInsightRow = useAtomValue(selectedInsightRowAtom);

  const audioBegin = Number(finding.audioBegin);
  const audioEnd = Number(finding.audioEnd);

  const onPlayInsightAudio = (insight) => {
    if (
      playedFindingId === insight?.findingID &&
      !dashJSPlayer?.playBackCompleted
    ) {
      dashJSPlayer.instance.play();
    } else {
      dashJSPlayer.loadMediaByInterviewID(
        insight?.interviewID,
        Number(insight?.audioBegin)
      );
      dashJSPlayer.play(null, Number(insight?.audioEnd));
    }
  };

  const iconColor =
    selectedInsightRow?.findingID === finding.findingID && !isDetails
      ? "white"
      : undefined;

  return audioBegin > 0 && audioEnd > 0 ? (
    <Tooltip title={dashJSPlayer?.isPaused ? "Play audio" : "Pause audio"}>
      {!dashJSPlayer?.isPaused &&
      selectedInsightRow?.findingID === finding.findingID ? (
        <IconButton
          style={{
            color: iconColor
          }}
          onClick={() => {
            dashJSPlayer.instance.pause();
            setPlayedFindingId(finding?.findingID);
          }}
        >
          <PauseAudioIcon selected={selected ?? selectedInsightRow?.findingID === finding.findingID }/>
        </IconButton>
      ) : (
        <IconButton
          style={{
            color: iconColor
          }}
          onClick={() => onPlayInsightAudio(finding)}
        >
          <PlayAudioIcon selected={selected ?? selectedInsightRow?.findingID === finding.findingID }/>
        </IconButton>
      )}
    </Tooltip>
  ) : null;
}

export default function InsightTable({
  tableData,
  rowVirtualizerInstanceRef,
  artifactDetails,
  setMatchedInsightGroup
}: InsightTableProps) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { dashJSPlayer } = useDashJSPlayer();

  const isHome = pathname === "/";

  const [selectedInsightRow, setSelectedInsightRow] = useAtom(
    selectedInsightRowAtom
  );

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      #custom-expand-table-insight-page th.MuiTableCell-root.css-1kmwuhi-MuiTableCell-root {
        display: none !important;
        width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      #custom-expand-table-insight-page td.MuiTableCell-root.css-xtz1a5-MuiTableCell-root {
        display: none !important;
        width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      #custom-expand-table-insight-page col[data-col-id="mrt-row-expand-button"],
      #custom-expand-table-insight-page colgroup col[data-col-id="mrt-row-expand-button"] {
        display: none !important;
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
      }

      #custom-expand-table-insight-page .MuiTable-root colgroup col:first-of-type {
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
      }

      #custom-expand-table-insight-page .MuiTableHead-root tr th:first-of-type {
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        display: none !important;
      }

      #custom-expand-table-insight-page .MuiTableBody-root tr.MuiTableRow-root td:first-of-type {
        width: 0 !important;
        min-width: 0 !important;
        max-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const insightTableColumns = [
    {
      accessorKey: "photoURL",
      header: "Created By",
      size: 100,
      Cell: ({ row }) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <MaterialAvatar
              user={{
                name: row.original.findingID,
                photoURL: row.original.photoURL,
              }}
              sx={{ width: "45px", height: "45px" }}
            />
            {row.original.findingInsertionType && (
              <Stack
                className="canvas-badge-bar ai-indicator-badge"
                direction="row"
                alignItems="center"
                sx={{
                  backgroundColor: theme?.custom?.white,
                  padding: "1.5px 7px",
                  borderRadius: "15px",
                  border: "1px solid rgb(130,130,130)",
                  position: "absolute",
                  bottom: -8,
                }}
              >
                <Stack direction="row" spacing={0.15} alignItems="center">
                  {aiIndicationIconsAvatar?.[row.original.findingInsertionType]}
                </Stack>
              </Stack>
            )}
          </div>
        );
      },
    },
    {
      id: "created",
      accessorFn: (row) => moment(row.created).valueOf(),
      header: "Date",
      size: 120,
      sortingFn: (cellA, cellB) => {
        return (
          moment(cellA.original.created).valueOf() -
          moment(cellB.original.created).valueOf()
        );
      },
      Cell: (cell) => {
        return (
          <div>{moment(cell.row.original.created).format(getDateFormat())}</div>
        );
      },
    },
    {
      accessorKey: "interviewee",
      header: "Interviewee",
      size: 150,
      Cell: (cell) => {
        return <Typography noWrap>{cell.row.original.interviewee}</Typography>;
      },
    },
    {
      header: "Priority",
      accessorKey: "Priority",
      size: 150,
      filterVariant: "multi-select",
      Filter: ({ column }) => (
        <MultiSelectFilter
          column={column}
          placeholder="Filter by Priority"
        />
      ),
      accessorFn: (row) => priorityBadge(row?.rating?.global?.rating)?.label,
      Cell: (cell) => renderRatingColumn(cell.row.original?.rating?.global?.rating || 0),
      filterFn: (row, columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0)
          return true;
        const value = row.getValue(columnId);
        return filterValue.includes(value);
      },
    },
    {
      accessorKey: "quote",
      header: "Quote",
      size: 150,
      Cell: (cell) => {
        return (
          <Typography noWrap>{getQuote(cell.row.original.quote)}</Typography>
        );
      },
    },
    {
      accessorKey: "comment",
      header: "Comment",
      size: 150,
      Cell: (cell) => {
        return <Typography noWrap>{cell.row.original.comment}</Typography>;
      },
    },
    {
      accessorFn: (row) =>
        row.links.map((link) => link?.category?.abbreviation).join(" "),
      header: "Links",
      filterVariant: "multi-select",
      filterFn: (row, columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
        const value: string = row.getValue(columnId) ?? "";
        return filterValue.some((filter) => value.includes(filter));
      },
      Filter: ({ column }) => (
        <MultiSelectFilter column={column} placeholder="Filter by Links" />
      ),

      Header: () => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* @ts-ignore */}
          <LinkIcon
            sx={{
              mt: !isHome ? "0px" : "1.5px",
              color: "inherit",
              fill: "none",
            }}
            strokeWidth="1.2"
          />
        </Box>
      ),
      size: 80,
      Cell: (cell) => {
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", flexDirection: "column", }}>
            {breakoutLinksColumns(cell.row.original?.links)}
          </Box>
        );
      },
    },
    {
      accessorFn: (row) => row.tags.map((item) => item.label).join(" "),
      header: "Tags",
      filterVariant: "multi-select",
      size: 150,
      filterFn: (row, columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
        const value: string = row.getValue(columnId) ?? "";
        return filterValue.some((filter) => value.includes(filter));
      },
      Filter: ({ column }) => (
        <MultiSelectFilter column={column} placeholder="Filter by Tags" />
      ),
      Cell: (cell) => {
        return (
          <Stack spacing={1}>
            {renderFindingTypes(cell.row.original.tags, theme)}
          </Stack>
        );
      },
    },
    {
      accessorFn: (row) => {
        for (const category in artifactDetails) {
          const entry = artifactDetails[category]?.[row?.interviewID];
          if (entry?.artifactID === row?.artifactID) {
            return artifactSourceTypeMap[category] ?? null;
          }
        }
        return null;
      },
      id: "source",
      header: "Source",
      filterVariant: "multi-select",
      filterFn: (row, columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0)
          return true;
        const value: string = row.getValue(columnId) ?? "";
        return filterValue.includes(value);
      },
      Filter: ({ column }) => (
        <MultiSelectFilter
          column={column}
          placeholder="Filter by Source"
        />
      ),
      size: 100,
      Cell: ({ row }) => {
        const sourceLabel = row.getValue("source");
        if (!sourceLabel) return null;

        return (
            <Chip
              label={sourceLabel}
              variant="outlined"
              sx={{
                borderRadius: "4px",
                height: "28px",
                fontSize: "15px",
                width:"100px"
              }}
            />
        );
      },
    },
  ] as MRT_ColumnDef<GetFindingTableResponse>[];

  const columns = useMemo<MRT_ColumnDef<GetFindingTableResponse>[]>(() => {
    if (!isHome) {
      insightTableColumns.splice(0, 0, {
        id: "findingID",
        accessorKey: "findingID",
        header: "",
        size: 45,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => {
          const hasSubRows = Array.isArray(row.original?.childFindings) && row.original?.childFindings?.length > 0;
          const isSelected = selectedInsightRow?.findingID === row.original.findingID;
          return hasSubRows ? (
            <IconButton 
              onClick={row.getToggleExpandedHandler()}
              sx={{
                borderRadius: "4px",
                padding:0,
                color: isSelected ? "#F6F6F6" : "inherit",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "action.hover",
                  color: isSelected ? "#F6F6F6" : "inherit", // or use a custom color
                },
              }}
            >
               <ArrowDropDownIcon style={{width : "40px", height : "40px", transform: row.getIsExpanded() ? `rotate(0deg)`: `rotate(-90deg)`}}/> 
            </IconButton>
          ) : (
            <span style={{ paddingLeft: row.depth > 0 ? `${row.depth * 6}px` : "0px", }}>
               <InsightAudio finding={row.original}/>
            </span>
          );
        },
      });
    }

    return insightTableColumns;
  }, [pathname]);

  const table = useMaterialReactTable({
    columns,
    data: tableData || [],
    rowVirtualizerInstanceRef,
    initialState: { 
      showColumnFilters: !isHome ? true : false,
      columnVisibility: {
        'mrt-row-expand-button': false,
        photoURL: false,
        created: false,
        source: false,
      },
    },
    enableFacetedValues: !isHome ? true : false,
    enableBottomToolbar: false,
    enableStickyHeader: true,
    enablePagination: false,
    enableFullScreenToggle: false,
    enableRowVirtualization: true,
    enableGlobalFilterModes: false,
    enableColumnActions: !isHome ? true : false,
    enableDensityToggle: !isHome ? true : false,
    enableTopToolbar: !isHome ? true : false,
    enableColumnVirtualization: true,
    defaultDisplayColumn: { enableResizing: true },
    enableMultiSort: false,
    manualSorting: false,
    enableHiding: true,
    enableColumnPinning: !isHome ? true : false,
    enableExpanding: true,
    getSubRows: (row) => {
      return row?.childFindings ?? []
    },
    filterFromLeafRows: true,
    enableExpandAll: false,
    muiTableHeadProps: {
      sx: {
        opacity: "unset !important",
      }
    },
    muiTableHeadRowProps: {
      sx: {
        // opacity: "unset !important",
        // bgcolor: "red !important",
        borderBottom: `1px solid inherit !important`,
        // paddingBottom: "30px",
        height: "auto !important",
        minHeight: "unset !important",
        alignItems: "flex-start",
        boxShadow: "none !important",
        "& tr": {
          boxShadow: "none !important"
        },
        "& .MuiTableCell-root": {
          whiteSpace: "normal",
          height: "auto",
          verticalAlign: "top"
        }
      }
    },
    renderTopToolbarCustomActions: () => (
      <>
        {!isHome ? (
          <Typography variant="h6" component="div">
            Insights ({tableData?.length})
            {tableData?.length > 0 ? (
              <Tooltip title="Export">
                <IconButton
                  onClick={() => {
                    handleExport(tableData);
                  }}
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Typography>
        ) : (
          <></>
        )}
      </>
    ),
    muiTablePaperProps: {
      sx: {
        backgroundColor: "transparent !important"
      }
    },
    muiTopToolbarProps: {
      sx: {
        bgcolor: "transparent !important"
      }
    },

    muiTableContainerProps: {
      id: 'custom-expand-table-insight-page',
      sx: {
        backgroundColor: "white",
        height: pathname === "/" ? "380px" : "calc(100vh - 130px)",
        maxHeight: pathname === "/" ? "380px" : "calc(100vh - 130px)",
        border: `1px solid ${theme?.custom?.borderGrey}`,
        borderRadius: "2px"
      }
    },
    getRowId: (row) => row.findingID,
    muiTableBodyRowProps: ({ row, staticRowIndex }) => ({
      hover: false,
      selected:
        selectedInsightRow?.findingID === row.original.findingID &&
        pathname !== "/",
      onClick: () => {
        setMatchedInsightGroup(null);
        setSelectedInsightRow({
          ...row.original,
          rowIndex: staticRowIndex
        });
        if (pathname === "/") {
          navigate("/insights");
        } else {
          dashJSPlayer.reset();
        }
      },
      sx: {
        cursor: "pointer",
        backgroundColor:
          pathname !== "/" &&
          selectedInsightRow?.findingID === row.original.findingID
            ? "rgb(25, 118, 210) !important"
            : undefined,
        ":hover": { backgroundColor: theme.custom.hoverOptionColor },
      }
    }),
    displayColumnDefOptions: {
      'mrt-row-expand': {
        enablePinning: false, 
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "none !important"
      }
    },
    muiTableBodyCellProps: (cell) => ({
      sx: {
        height: "auto !important",
        py: "15px !important",
        color:
          pathname !== "/" &&
          selectedInsightRow?.findingID === cell.row.original.findingID
            ? "white"
            : undefined
      }
    })
  });

  useEffect(() => {
    if (
      tableData?.length > 0 &&
      selectedInsightRow === null &&
      pathname !== "/"
    ) {
      setSelectedInsightRow(tableData[0]);
    }
  }, [tableData]);

  useEffect(() => {
    if (
      selectedInsightRow &&
      rowVirtualizerInstanceRef.current &&
      pathname !== "/" &&
      selectedInsightRow?.rowIndex !== -1
    ) {
       const scrollIndex = selectedInsightRow.rowIndex;
       requestAnimationFrame(() => {
          rowVirtualizerInstanceRef.current.scrollToIndex(scrollIndex, {
            align: "center",
          });
       });
    }
  }, [tableData, pathname, rowVirtualizerInstanceRef,selectedInsightRow]);

  return <MaterialReactTable table={table} />;
}
